import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { RefreshCw, Eye, EyeOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

type JoinStep = "loading" | "invalid" | "already_accepted" | "create_password" | "profile_info" | "done";

const cardClass = "bg-slate-900 border-slate-800 text-slate-100 w-full max-w-md";

const initialsOf = (first: string, last: string, email: string): string => {
  const a = (first || "").trim().charAt(0);
  const b = (last || "").trim().charAt(0);
  const fallback = (email || "?").trim().charAt(0);
  return (a + b || fallback).toUpperCase();
};

export default function Join() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const invitationId = searchParams.get("superuser_invitation");
  const token = searchParams.get("token");

  const [step, setStep] = useState<JoinStep>("loading");
  const stepRef = useRef<JoinStep>("loading");
  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  const [invalidMessage, setInvalidMessage] = useState<string>("Invalid invitation link");
  const [invitationEmail, setInvitationEmail] = useState<string>("");

  // Step 1 state
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [submittingPw, setSubmittingPw] = useState(false);

  // Step 2 state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("");
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [submittingProfile, setSubmittingProfile] = useState(false);
  const [doneMessage, setDoneMessage] = useState<string>("");

  // Validate invitation on mount
  useEffect(() => {
    let cancelled = false;

    const validate = async () => {
      if (!invitationId || !token) {
        setInvalidMessage("Invalid invitation link");
        setStep("invalid");
        return;
      }

      try {
        const { data, error } = await supabase.rpc(
          "get_superuser_invitation_by_token" as any,
          { p_invitation_id: invitationId, p_token: token } as any,
        );

        if (cancelled) return;

        if (error || !data) {
          setInvalidMessage("This invitation is invalid or has expired.");
          setStep("invalid");
          return;
        }

        const row: any = Array.isArray(data) ? data[0] : data;
        if (!row) {
          setInvalidMessage("This invitation is invalid or has expired.");
          setStep("invalid");
          return;
        }

        if (row.status === "accepted") {
          setStep("already_accepted");
          return;
        }

        if (row.status === "expired" || row.status === "cancelled") {
          setInvalidMessage("This invitation link is no longer valid.");
          setStep("invalid");
          return;
        }

        if (row.status !== "pending") {
          setInvalidMessage("This invitation is invalid or has expired.");
          setStep("invalid");
          return;
        }

        // Check if invitation has expired by date even if status is still pending
        if (row.expires_at && new Date(row.expires_at) < new Date()) {
          setInvalidMessage("This invitation has expired. Please ask an administrator to send a new one.");
          setStep("invalid");
          return;
        }

        const email: string = row.email || "";
        setInvitationEmail(email);

        const { data: { session } } = await supabase.auth.getSession();
        if (cancelled) return;

        if (session) {
          if ((session.user.email || "").toLowerCase() === email.toLowerCase()) {
            setStep("profile_info");
            return;
          }
          await supabase.auth.signOut();
        }
        setStep("create_password");
      } catch (err) {
        if (cancelled) return;
        setInvalidMessage("This invitation is invalid or has expired.");
        setStep("invalid");
      }
    };

    validate();
    return () => {
      cancelled = true;
    };
  }, [invitationId, token]);

  // Watch auth state to advance step
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const email = session?.user?.email?.toLowerCase();
      if (email && invitationEmail && email === invitationEmail.toLowerCase()) {
        if (stepRef.current === "create_password") {
          setStep("profile_info");
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [invitationEmail]);

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);

    const previewObjectUrl = URL.createObjectURL(file);
    setPhotoPreview(previewObjectUrl);

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const userId = session.user.id;
    const ext = file.type.split('/')[1]?.replace('jpeg', 'jpg') ?? 'jpg';
    const path = `${userId}/avatar.${ext}`;

    try {
      const { error: uploadError } = await supabase.storage
        .from('profile-photos')
        .upload(path, file, { upsert: true, contentType: file.type });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('profile-photos')
        .getPublicUrl(path);

      setPhotoUrl(data.publicUrl);
    } catch (err) {
      console.error('Photo upload error:', err);
    }
  };

const handleCreatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwError(null);
    if (password.length < 8) {
      setPwError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setPwError("Passwords do not match.");
      return;
    }
    setSubmittingPw(true);
    try {
      const { error: fnError } = await supabase.functions.invoke("create-invited-user", {
        body: {
          email: invitationEmail,
          password,
          invitationId,
          token,
        },
      });
      if (fnError) {
        const msg = fnError.message || "";
        const isAlreadyExists =
          msg.includes("409") ||
          msg.toLowerCase().includes("already exists");
        if (isAlreadyExists) {
          const { data: signInData } = await supabase.auth.signInWithPassword({
            email: invitationEmail,
            password,
          });
          if (signInData?.session) {
            const { error: acceptError } = await supabase.rpc(
              "accept_superuser_invitation" as any,
              { p_invitation_id: invitationId } as any,
            );
            if (acceptError) {
              console.warn("accept_superuser_invitation:", acceptError.message);
            }
            setStep("profile_info");
            return;
          }
          setPwError(
            "An account already exists for this email. If you set up a password previously, please use that. If you forgot it, contact your administrator."
          );
          return;
        }
        setPwError(fnError.message || "Failed to create account.");
        return;
      }
      // Sign in to ensure session is set
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email: invitationEmail,
        password,
      });
      if (signInError || !signInData.session) {
        // onAuthStateChange may still fire; otherwise show error
        setPwError(signInError?.message || "Account created, but sign-in failed.");
        return;
      }
      const { error: acceptError } = await supabase.rpc(
        "accept_superuser_invitation" as any,
        { p_invitation_id: invitationId } as any,
      );
      if (acceptError) {
        console.warn("accept_superuser_invitation:", acceptError.message);
      }
      setStep("profile_info");
    } catch (err) {
      setPwError(err instanceof Error ? err.message : "Failed to create account.");
    } finally {
      setSubmittingPw(false);
    }
  };

  const handleCompleteProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileError(null);
    if (!firstName.trim() || !lastName.trim() || !phone.trim() || !role.trim()) {
      setProfileError("All fields are required.");
      return;
    }
    setSubmittingProfile(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setProfileError("Session expired. Please refresh and try again.");
        return;
      }
      const userId = session.user.id;

      const { error: profileErr } = await supabase
        .from("profiles")
        .update({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          company_position: role.trim(),
          personal_phone_number: phone.trim(),
          profile_photo_url: photoUrl,
          profile_completed: true,
          updated_at: new Date().toISOString(),
        })
        .eq("id", userId);
      if (profileErr) throw profileErr;

      const { error: acceptError } = await supabase.rpc(
        "accept_superuser_invitation" as any,
        { p_invitation_id: invitationId } as any,
      );
      if (acceptError) {
        console.warn("accept_superuser_invitation:", acceptError.message);
      }

      // Force session refresh so ProtectedRoute recognises new superuser role
      await supabase.auth.refreshSession();

      setDoneMessage("You now have superuser access. Redirecting...");
      setStep("done");
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 1500);
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : "Failed to complete profile.");
    } finally {
      setSubmittingProfile(false);
    }
  };

  return (
    <main className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      {step === "loading" && (
        <div className="text-slate-300 flex items-center gap-3">
          <RefreshCw className="h-6 w-6 animate-spin" />
          <span>Loading invitation…</span>
        </div>
      )}

      {step === "invalid" && (
        <Card className={cardClass}>
          <CardHeader>
            <CardTitle className="text-white">Invitation unavailable</CardTitle>
            <CardDescription className="text-slate-400">{invalidMessage}</CardDescription>
          </CardHeader>
          <CardFooter>
            <a
              href="https://admin.doyencegroup.com"
              className="text-amber-400 hover:text-amber-300 text-sm"
            >
              Return to admin.doyencegroup.com
            </a>
          </CardFooter>
        </Card>
      )}

      {step === "already_accepted" && (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
          <Card className="w-full max-w-md bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white">Invitation Already Used</CardTitle>
              <CardDescription className="text-slate-400">
                This invitation has already been accepted. If you have an account, sign in to access the admin portal.
              </CardDescription>
            </CardHeader>
            <CardFooter>
              <Button
                className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950"
                onClick={() => navigate('/')}
              >
                Go to Sign In
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}

      {step === "create_password" && (
        <Card className={cardClass}>
          <CardHeader>
            <CardTitle className="text-white">Welcome to Doyence Admin</CardTitle>
            <CardDescription className="text-slate-400">
              You've been invited as a platform superuser. Create a password to get started.
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleCreatePassword}>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="join-email" className="text-slate-300">Email</Label>
                <Input
                  id="join-email"
                  type="email"
                  value={invitationEmail}
                  readOnly
                  className="bg-slate-800 border-slate-700 text-slate-400"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="join-password" className="text-slate-300">Password</Label>
                <div className="relative">
                  <Input
                    id="join-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    className="bg-slate-950 border-slate-700 text-slate-100 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="join-confirm" className="text-slate-300">Confirm password</Label>
                <div className="relative">
                  <Input
                    id="join-confirm"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    className="bg-slate-950 border-slate-700 text-slate-100 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              {pwError && (
                <p className="text-sm text-red-400">{pwError}</p>
              )}
            </CardContent>
            <CardFooter>
              <Button
                type="submit"
                disabled={submittingPw}
                className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950"
              >
                {submittingPw ? "Creating account…" : "Create Account"}
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}

      {step === "profile_info" && (
        <Card className={cardClass}>
          <CardHeader>
            <CardTitle className="text-white">Complete Your Profile</CardTitle>
            <CardDescription className="text-slate-400">
              Tell us about yourself before accessing the admin portal.
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleCompleteProfile}>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="relative">
                  {photoPreview ? (
                    <img
                      src={photoPreview}
                      alt="Profile preview"
                      className="h-16 w-16 rounded-full object-cover border border-slate-700"
                    />
                  ) : (
                    <div className="h-16 w-16 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-300 text-lg font-semibold">
                      {initialsOf(firstName, lastName, invitationEmail)}
                    </div>
                  )}
                </div>
                <div>
                  <Label
                    htmlFor="join-photo"
                    className="text-amber-400 hover:text-amber-300 text-sm cursor-pointer"
                  >
                    {photoFile ? "Change photo" : "Upload photo"}
                  </Label>
                  <input
                    id="join-photo"
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoSelect}
                    className="hidden"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="join-first" className="text-slate-300">First name</Label>
                  <Input
                    id="join-first"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    className="bg-slate-950 border-slate-700 text-slate-100"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="join-last" className="text-slate-300">Last name</Label>
                  <Input
                    id="join-last"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    className="bg-slate-950 border-slate-700 text-slate-100"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="join-phone" className="text-slate-300">Phone number</Label>
                <Input
                  id="join-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  className="bg-slate-950 border-slate-700 text-slate-100"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="join-role" className="text-slate-300">Your role / position</Label>
                <Input
                  id="join-role"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  placeholder="e.g. Platform Manager, CTO"
                  required
                  className="bg-slate-950 border-slate-700 text-slate-100"
                />
              </div>

              {profileError && (
                <p className="text-sm text-red-400">{profileError}</p>
              )}
            </CardContent>
            <CardFooter>
              <Button
                type="submit"
                disabled={submittingProfile}
                className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950"
              >
                {submittingProfile ? "Saving…" : "Complete Setup"}
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}

      {step === "done" && (
        <Card className={cardClass}>
          <CardHeader>
            <CardTitle className="text-white">All set</CardTitle>
            <CardDescription className="text-slate-400">{doneMessage}</CardDescription>
          </CardHeader>
        </Card>
      )}
    </main>
  );
}
